import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { lastValueFrom } from 'rxjs';
import { FingerprintService } from 'src/app/services/fingerprint.service';
import { FingerprintLocalApiService } from 'src/app/services/fingerprint-local-api.service';
import { TranslateService } from '@ngx-translate/core';
import { FingerprintRegisterDialogComponent } from '../fingerprint-register-dialog/fingerprint-register-dialog.component';
import { CameraDialogComponent } from '../camera-dialog/camera-dialog.component';
import { SnackbarMessageComponent } from '../snackbar-message/snackbar-message.component';

export enum FINGERPRINT_STATE {
	HAS_FINGERPRINT = 'HAS_FINGERPRINT',
	NO_FINGERPRINT_MEMBER = 'NO_FINGERPRINT_MEMBER',
	NOT_MEMBER = 'NOT_MEMBER'
}

export enum BIOMETRIC_TYPE {
	FINGERPRINT = 'FINGERPRINT',
	FACE = 'FACE'
}

@Component({
	selector: 'app-fingerprint-step',
	templateUrl: './fingerprint-step.component.html',
	styleUrls: ['./fingerprint-step.component.scss']
})
export class FingerprintStepComponent implements OnInit, OnChanges {
	FINGERPRINT_STATE = FINGERPRINT_STATE;
	BIOMETRIC_TYPE = BIOMETRIC_TYPE;
	@Input() verified: boolean = false;
	@Input() isSkipped: boolean = false;

	@Output() stepCompleted = new EventEmitter<{ verified: boolean, skipped: boolean }>();
	@Output() verifiedSuccess = new EventEmitter<void>();

	selectedType: BIOMETRIC_TYPE = BIOMETRIC_TYPE.FINGERPRINT;

	state: FINGERPRINT_STATE | null = null;
	registered: boolean = false;
	isChecking: boolean = true;
	isCheckingFace: boolean = false;
	isFingerprintVerifying: boolean = false;
	hasPhoto: boolean = false;
	private _hasFaceChecked: boolean = false;
	hasConnectionError: boolean = false;
	fingerprint_records: any[] = [];

	// Face capture states
	photoFile?: Blob | File;
	photoSource?: SafeResourceUrl;
	isFaceVerifying: boolean = false;
	isFaceVerified: boolean = false;

	@Input() idcard: string | null = null;
	@Input() ben_id: string | null = null;
	@Input() code: string | null = null;
	@Input() claim_request_id: string | null = null;

	constructor(
		private _fingerprintService: FingerprintService,
		private _localApiService: FingerprintLocalApiService,
		private _translateService: TranslateService,
		private _dialog: MatDialog,
		private _cd: ChangeDetectorRef,
		private _sanitizer: DomSanitizer,
		private _snackbar: MatSnackBar
	) { }

	ngOnInit(): void { }

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['idcard'] && !changes['ben_id']) {
			this.ben_id = null;
		}
		if (changes['idcard'] || changes['ben_id'] || changes['claim_request_id']) {
			this._hasFaceChecked = false;
			this.checkStatus();
			if (this.selectedType === BIOMETRIC_TYPE.FACE) {
				this.checkFaceStatus();
			}
		}
	}

	async onTypeChange(type: BIOMETRIC_TYPE) {
		this.selectedType = type;
		if (type === BIOMETRIC_TYPE.FACE && !this._hasFaceChecked) {
			await this.checkFaceStatus();
		}
		this._cd.markForCheck();
	}

	getFormattedBenId(): string | null {
		const rawVal = this.code;
		if (!rawVal) return null;
		if (rawVal.includes('-')) {
			const parts = rawVal.split('-');
			if (parts.length > 1 && parts[1]?.trim()) {
				return parts[1].trim();
			}
		}
		return rawVal.trim();
	}

	async checkFaceStatus() {
		this.isCheckingFace = true;
		this._hasFaceChecked = true;
		try {
			const res = await lastValueFrom(this._fingerprintService.getFaceData(this.ben_id));
			if (res && res.status === 1 && res.data) {
				this.hasPhoto = !!res.data.has_photo;
			} else {
				this.hasPhoto = false;
			}
		} catch (error) {
			console.error("Check face error:", error);
			this.hasPhoto = false;
		} finally {
			this.isCheckingFace = false;
			this._cd.markForCheck();
		}
	}

	async checkStatus() {
		this.isChecking = true;
		this.hasConnectionError = false;
		this.fingerprint_records = [];
		try {
			const checkRes = await lastValueFrom(
				this._fingerprintService.checkFingerprint(this.idcard, this.ben_id, this.claim_request_id)
			);
			if (checkRes && checkRes.status === 1 && checkRes.data) {
				if (checkRes.data.ben_id) {
					this.ben_id = checkRes.data.ben_id;
				}
				if (checkRes.data.has_fingerprint) {
					this.state = FINGERPRINT_STATE.HAS_FINGERPRINT;
				} else if (checkRes.data.is_member) {
					this.state = FINGERPRINT_STATE.NO_FINGERPRINT_MEMBER;
				} else {
					this.state = FINGERPRINT_STATE.NOT_MEMBER;
				}
			}
		} catch (error) {
			console.error("Load fingerprint status failed:", error);
			this.hasConnectionError = true;
		} finally {
			this.isChecking = false;
			this.stepCompleted.emit({ verified: this.verified, skipped: this.isSkipped });
			this._cd.markForCheck();
		}
	}

	async verifyFingerprint() {
		this.isFingerprintVerifying = true;
		try {
			// 1. Get templates from BE proxy API
			const response = await lastValueFrom(
				this._fingerprintService.getFingerprintData(this.idcard, this.ben_id, this.claim_request_id)
			);

			if (response.status !== 1) {
				this._openSnackbar('fingerprint.verify_fail', true);
				return;
			}

			if (!response.data || !response.data.length) {
				this._openSnackbar('fingerprint.not_found', true);
				return;
			}

			const templates = response.data
				.map((f: any) => (typeof f === 'string' ? f : f.finger_template))
				.filter((t: any) => t);

			if (!templates.length) {
				this._openSnackbar('fingerprint.not_found', true);
				return;
			}

			// 2. Call local scanner app to verify
			const verifyRes = await lastValueFrom(this._localApiService.verifyFingerprint(templates));
			if (verifyRes && verifyRes.is_verify) {
				this.verified = true;
				this._openSnackbar('fingerprint.verify_match', false);
				this.stepCompleted.emit({ verified: true, skipped: this.isSkipped });
				this.verifiedSuccess.emit();
			} else {
				this._openSnackbar('fingerprint.verify_not_match', true);
			}
		} catch (error) {
			console.error("Fingerprint verification error:", error);
			this._openSnackbar('fingerprint.verify_fail', true);
		} finally {
			this.isFingerprintVerifying = false;
			this._cd.markForCheck();
		}
	}

	private _openSnackbar(messageKey: string, error?: boolean) {
		this._translateService.get(messageKey).subscribe((translation: string) => {
			this._snackbar.open(translation, '', {
				duration: 3000,
				horizontalPosition: 'center',
				panelClass: error ? 'panel-error' : 'panel-success'
			});
		});
	}

	openRegister() {
		this._dialog.open(FingerprintRegisterDialogComponent, {
			width: '850px',
			autoFocus: false,
			disableClose: true,
			data: {
				idcard: this.idcard,
				ben_id: this.ben_id,
				claim_request_id: this.claim_request_id
			}
		}).afterClosed().subscribe((registered: boolean) => {
			if (registered) {
				this.registered = true;
				// After registration, update state to HAS_FINGERPRINT
				this.state = FINGERPRINT_STATE.HAS_FINGERPRINT;
				// Mandatory verification is required next
				this.verified = false;
				this.checkStatus();
				this.stepCompleted.emit({ verified: false, skipped: this.isSkipped });
			}
		});
	}

	onSkipChange(event: any) {
		this.stepCompleted.emit({ verified: this.verified, skipped: this.isSkipped });
	}

	async onOpenCamera() {
		const dialogRef = this._dialog.open(CameraDialogComponent, {
			width: '998px',
			disableClose: true,
			autoFocus: false
		});
		const photoBlob = await lastValueFrom(dialogRef.afterClosed());
		if (photoBlob) {
			this.photoFile = photoBlob;
			this.photoSource = this._sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(photoBlob));
			this._cd.detectChanges();
		}
	}

	onFileChange(file: File) {
		if (!file) return;
		if (!/image\/.*/.test(file.type)) {
			this._snackbar.openFromComponent(SnackbarMessageComponent, {
				data: { message: 'general.file_unsupported' },
				duration: 2000,
				panelClass: 'app-error'
			});
			return;
		}

		const reader = new FileReader();
		reader.onload = (e: any) => {
			this.photoFile = file;
			this.photoSource = this._sanitizer.bypassSecurityTrustResourceUrl(e.target.result);
			this._cd.detectChanges();
		};
		reader.readAsDataURL(file);
	}

	async verifyFace() {
		if (!this.photoFile) {
			this._snackbar.openFromComponent(SnackbarMessageComponent, {
				data: { message: 'face.please_capture' },
				duration: 2500,
				panelClass: 'app-warning'
			});
			return;
		}

		this.isFaceVerifying = true;
		this._cd.detectChanges();

		try {
			const targetBenId = this.getFormattedBenId();
			const res = await lastValueFrom(
				this._fingerprintService.verifyFace(this.photoFile, targetBenId, this.claim_request_id)
			);

			if (res && res.status === 1) {
				this.verified = true;
				this.isFaceVerified = true;
				this._snackbar.openFromComponent(SnackbarMessageComponent, {
					data: { message: 'face.verify_match' },
					duration: 3000,
					panelClass: 'app-success'
				});
				this.stepCompleted.emit({ verified: true, skipped: false });
				this.verifiedSuccess.emit();
			} else {
				this._snackbar.openFromComponent(SnackbarMessageComponent, {
					data: { message: res?.message || 'face.verify_not_match' },
					duration: 3000,
					panelClass: 'app-error'
				});
			}
		} catch (error: any) {
			console.error("Face verification failed:", error);
			this._snackbar.openFromComponent(SnackbarMessageComponent, {
				data: { message: error?.message || 'face.verify_fail' },
				duration: 3000,
				panelClass: 'app-error'
			});
		} finally {
			this.isFaceVerifying = false;
			this._cd.detectChanges();
		}
	}
}

